import React from "react";
import { fmtHoursSigned } from "@/lib/constants";

/**
 * SuiteTable — wrapper standard sopra le tabelle Shadcn per applicare in modo
 * uniforme lo standard Enterprise Suite:
 *  - Righe alte (py-3), scala tipografica text-sm coerente
 *  - Header sticky (rimane fisso durante lo scroll verticale)
 *  - Prima colonna bloccata (sticky left-0) — opzionale via prop `lockFirstCol`
 *  - Hover soft su tutte le righe
 *  - Corner radius e border coerenti con le SuiteCard
 *
 * Utilizzo (drop-in):
 *   <SuiteTable maxHeight="calc(100vh - 300px)" lockFirstCol>
 *     <thead> ... con <SuiteTh /> ... </thead>
 *     <tbody> <tr> <SuiteTd first>...</SuiteTd> ... </tr> </tbody>
 *   </SuiteTable>
 */
export function SuiteTable({
  children,
  className = "",
  maxHeight,
  lockFirstCol = false,
  minWidth,
  testid,
  ...rest
}) {
  const style = {};
  if (maxHeight) style.maxHeight = maxHeight;
  return (
    <div
      className={`relative rounded-md border border-suite-neutral-200 bg-suite-surface overflow-auto ${className}`}
      style={style}
      data-testid={testid}
      data-lock-first={lockFirstCol ? "1" : undefined}
      {...rest}
    >
      <table
        className={`w-full text-sm border-separate border-spacing-0 ${lockFirstCol ? "suite-table-lock-first" : ""}`}
        style={minWidth ? { minWidth } : undefined}
      >
        {children}
      </table>
    </div>
  );
}

/** Cella header sticky. `first` la ancora anche a sinistra (angolo alto-sinistra). */
export function SuiteTh({ children, className = "", first = false, align = "left", ...rest }) {
  const alignCls = align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";
  const stickyLeft = first ? "left-0 z-30 shadow-[1px_0_0_rgba(0,0,0,0.06)]" : "";
  return (
    <th
      className={[
        "sticky top-0 z-20 bg-suite-neutral-50/95 backdrop-blur",
        "px-3 py-2.5 text-[10px] uppercase tracking-[0.15em] font-semibold text-suite-neutral-600",
        "border-b border-suite-neutral-200",
        alignCls,
        stickyLeft,
        className,
      ].join(" ")}
      {...rest}
    >
      {children}
    </th>
  );
}

/** Cella body con altezza generosa. `first` la mantiene sticky a sinistra. */
export function SuiteTd({ children, className = "", first = false, align = "left", mono = false, ...rest }) {
  const alignCls = align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";
  const stickyLeft = first ? "sticky left-0 z-10 bg-suite-surface group-hover:bg-suite-surface-hover shadow-[1px_0_0_rgba(0,0,0,0.06)]" : "";
  return (
    <td
      className={[
        "px-3 py-3 text-sm border-b border-suite-neutral-100 text-suite-neutral-800",
        mono ? "font-mono tabular-nums" : "",
        alignCls,
        stickyLeft,
        className,
      ].join(" ")}
      {...rest}
    >
      {children}
    </td>
  );
}

/**
 * SuiteBalanceCell — cella specializzata per saldi banca ore.
 * Colori: verde (positivo), rosso (negativo), grigio (zero o nullo).
 *
 * Nota implementativa: usiamo il prefix `!` (important) sulle utility di colore
 * per vincere il tie-break di specificità con `text-suite-neutral-800` che
 * SuiteTd applica come default a tutte le celle body. Senza `!`, la classe
 * neutral-800 di SuiteTd (dichiarata dopo nel CSS compilato di Tailwind JIT)
 * sovrascriveva sempre le tone colors — verificato via getComputedStyle in
 * iteration_19.json.
 */
export function SuiteBalanceCell({ value, showZero = true, className = "", ...rest }) {
  const num = Number(value);
  const isZero = !num || Math.abs(num) < 0.01;
  const tone = isZero
    ? "!text-suite-neutral-400"
    : num > 0
    ? "!text-suite-accent-hover"
    : "!text-suite-error";
  const display = isZero && !showZero ? "—" : fmtHoursSigned(num || 0);
  return (
    <SuiteTd align="right" mono className={`font-semibold ${tone} ${className}`} {...rest}>
      {display}
    </SuiteTd>
  );
}

export default SuiteTable;
